/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { Grid, Paper, TextField, ThemeProvider } from "@mui/material";
import { SEText } from "../../../../components/SEText";
import { Controller, useForm } from "react-hook-form";
import { BlacklistSSEInterface } from "../../../../interfaces";
import { BlacklistSSEService } from "../services";
import useRenderFormStore from "../../stores/useRenderFormStore";
import { BaSeDialogContext } from "@base/react";
import useFormMutation from "../../hooks/useFormMutation";
import theme from "../../../../mui-theme";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router";
import { RiSearchLine } from "react-icons/ri";
import { useQuery } from "react-query";
import { handleModalStatus } from "../../../../../utils/handleModalStatus";
import { Button } from "@material-ui/core";
import { SEFormUserAction } from "../../../../components/SEFormUserAction";
import SEFilterSelect from "../../../../components/SEFilterSelect";
import { valueIsRequired } from "../../../../../utils/regex";
import { sortArrayByProp } from "../../../../utils/utils";

const CustomIcon = styled(RiSearchLine)(({ theme }) => ({
  fill: theme.palette.primary.main,
  height: 26,
  width: 26,
  top: "calc(50% - .75em) !important",
}));

const FormularioBlacklistSSE = () => {
  const { showModal } = React.useContext(BaSeDialogContext);
  const history = useHistory();
  const location = useLocation();
  const hasCopyParam = location.pathname.includes("/copy");

  const { data: ufResponse } = useQuery({
    queryFn: () => BlacklistSSEService.findUfs(),
    queryKey: ["ufResponseBlacklistSSE"],
  });

  const { formData: editFormData, handleShowForm } = useRenderFormStore();

  const {
    formState: { isValid, errors },
    control,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      nomeAtendente: "",
      protocoloSas: "",
      cnpj: "",
      uf: "",
    },
  });

  const onResetFields = () => {
    reset({
      nomeAtendente: "",
      protocoloSas: "",
      cnpj: "",
      uf: "",
    });
  };

  const goBack = () => {
    handleShowForm(false);
    history.push(`/tabelas-de-controle/tipos-registros`);
  };

  const { createMutation, updateMutation } = useFormMutation({
    showModal,
    service: BlacklistSSEService,
    onCloseModalSuccess: (res) => {
      if (!res.id) {
        handleModalStatus({
          type: "error",
          msgOnError: `Blacklist SSE não foi ${
            editFormData?.id ? "editada" : "adicionada"
          }. Aconteceu um erro ou o sistema está temporariamente fora do ar. Tente novamente mais tarde.`,
          // @ts-ignore
          showModal,
        });
      }

      onResetFields();
      goBack();
    },
    onCloseModalError: (error) => {
      handleModalStatus({
        type: "error",
        msgOnError: `Aconteceu um erro ou o sistema está temporariamente fora do ar. Tente novamente mais tarde.`,
        // @ts-ignore
        showModal,
      });
    },
    onCloseModalUpdate: () => goBack(),
  });

  const submitForm = async (dataForm: BlacklistSSEInterface) => {
    // @ts-ignore
    if (!isValid || errors.length) {
      return;
    }

    try {
      const body = {
        nomeAtendente: dataForm.nomeAtendente,
        protocoloSas: dataForm.protocoloSas,
        cnpj: dataForm.cnpj,
        uf: dataForm.uf,
      };

      if (dataForm.id) {
        const bodyToEdit = {
          id: dataForm.id,
          nomeAtendente: dataForm.nomeAtendente,
          protocoloSas: dataForm.protocoloSas,
          cnpj: dataForm.cnpj,
          uf: dataForm.uf,
        };
        // @ts-ignore
        return await updateMutation(bodyToEdit);
      }
      // @ts-ignore

      return await createMutation(body);
    } catch (e) {
      handleModalStatus({
        type: "error",
        // @ts-ignore
        showModal,
      });
    }
  };

  useEffect(() => {
    async function loadedData() {
      if (editFormData?.id) {
        reset(editFormData);
      }
    }

    loadedData();
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFormData]);

  return (
    <ThemeProvider theme={theme}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 2,
          paddingLeft: 4,
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <SEText
              as="h2"
              size={"medium"}
              font="Mulish"
              weight="bold"
              color="#7B80FF"
            >
              {editFormData?.id
                ? hasCopyParam
                  ? "Copiar"
                  : "Editar"
                : "Incluir"}
            </SEText>
          </Grid>
        </Grid>

        <Grid container spacing={2} marginTop={2} alignItems={"center"}>
          <Grid container spacing={3} alignItems="center">
            <Grid item md={12}>
              <Controller
                control={control}
                rules={{
                  ...valueIsRequired(),
                }}
                name="uf"
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <SEFilterSelect
                    id={"uf"}
                    defaultOption={"Selecione uma das opções"}
                    IconComponent={CustomIcon}
                    label="UF *"
                    value={value}
                    onChange={onChange}
                    options={sortArrayByProp(ufResponse, "sigla")}
                    displayField="sigla"
                    error={error}
                  />
                )}
              />
            </Grid>

            <Grid item md={12}>
              <Controller
                control={control}
                rules={{
                  ...valueIsRequired(),
                }}
                name="cnpj"
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <>
                    <TextField
                      id="cnpj"
                      label="CNPJ * "
                      placeholder="Digite o CNPJ"
                      fullWidth
                      value={value}
                      onChange={onChange}
                    />
                    {error && (
                      <p style={{ color: "red", marginTop: "8px" }}>
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </Grid>

            <Grid item md={12}>
              <Controller
                control={control}
                name="protocoloSas"
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <>
                    <TextField
                      id="protocoloSas"
                      label="Protocolo SAS"
                      placeholder="Digite o número do protocolo SAS"
                      fullWidth
                      value={value}
                      onChange={onChange}
                    />
                    {error && (
                      <p style={{ color: "red", marginTop: "8px" }}>
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </Grid>

            <Grid item md={12}>
              <Controller
                control={control}
                name="nomeAtendente"
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <>
                    <TextField
                      id="nomeAtendente"
                      label="Nome do agente"
                      placeholder="Digite o nome do agente"
                      fullWidth
                      value={value}
                      onChange={onChange}
                    />
                    {error && (
                      <p style={{ color: "red", marginTop: "8px" }}>
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </Grid>
          </Grid>

          <Grid
            item
            md={12}
            display="flex"
            justifyContent="flex-end"
            gap={2}
            marginTop={2}
          >
            <Button variant="outlined" onClick={goBack}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              style={{
                backgroundColor: "#7B80FF",
                color: "#FFFFFF",
                fontFamily: "Mulish, sans-serif",
              }}
              onClick={handleSubmit(submitForm)}
              disabled={false}
            >
              {editFormData?.id
                ? hasCopyParam
                  ? "Copiar"
                  : "Editar"
                : "Incluir"}
            </Button>
          </Grid>

          <Grid item md={12} marginTop={3}>
            <SEFormUserAction data={editFormData} />
          </Grid>
        </Grid>
      </Paper>
    </ThemeProvider>
  );
};

export default FormularioBlacklistSSE;
